/**
 * Resolve which visual asset actually conditions video generation.
 *
 * UI may show character / product / scene refs (`inputAssetRefs`) AND/OR
 * graph-connected materials (`inputMaterials`) AND/OR keyframe slots — only
 * a subset historically reached `reference_image_asset_id`. This module is
 * the single submit-time resolver (P0: no silent text-only drop).
 */

import type { InputMaterial, KeyframeAssignment, KeyframeSlotMaterial } from './dataflow'
import {
  keyframeMaterialForEntry,
  type KeyframeTimelineEntry,
  type InputAssetRef,
} from './videoKeyframeTimeline'
import { prioritizeRefsForKeyframe, refsWithImageAssets } from './videoShotReferenceAssets'

export type VisualConditioningKind =
  | 'none'
  | 'keyframe_start'
  | 'keyframe_timeline_start'
  | 'input_asset_ref'
  | 'input_material'

export type VisualSemanticRole = 'first_frame' | 'reference_image' | 'none'

export interface ResolvedVisualConditioning {
  kind: VisualConditioningKind
  semanticRole: VisualSemanticRole
  keyframeSlot?: KeyframeSlotMaterial
  inputAssetRef?: InputAssetRef
  inputMaterial?: Extract<InputMaterial, { kind: 'IMAGE' }>
  sourceLabel: string
}

export interface VisualConditioningDiagnostics {
  has_first_frame: boolean
  reference_asset_count: number
  reference_asset_types: string[]
  visual_conditioning_source: string
  visual_semantic_role: VisualSemanticRole
  prompt_source: 'enhanced' | 'original'
}

export function resolveVideoVisualConditioning(args: {
  keyframeAssignment: KeyframeAssignment
  keyframeTimeline: KeyframeTimelineEntry[]
  inputAssetRefs: InputAssetRef[]
  readyImageMaterials: Array<Extract<InputMaterial, { kind: 'IMAGE' }>>
  hasStartKeyframe: boolean
}): ResolvedVisualConditioning {
  if (args.keyframeAssignment.start?.ready) {
    return {
      kind: 'keyframe_start',
      semanticRole: 'first_frame',
      keyframeSlot: args.keyframeAssignment.start,
      sourceLabel: 'keyframe_assignment.start',
    }
  }

  const startEntry = args.keyframeTimeline.find((e) => e.role === 'start')
  if (startEntry) {
    const slot = keyframeMaterialForEntry(args.keyframeAssignment, startEntry)
    if (slot?.ready) {
      return {
        kind: 'keyframe_timeline_start',
        semanticRole: 'first_frame',
        keyframeSlot: slot,
        sourceLabel: 'keyframe_timeline.start',
      }
    }
  }

  const prioritizedRef = prioritizeRefsForKeyframe(args.inputAssetRefs, 1)[0]
  if (prioritizedRef?.assetId) {
    return {
      kind: 'input_asset_ref',
      semanticRole: 'first_frame',
      inputAssetRef: prioritizedRef,
      sourceLabel: `input_asset_ref.${prioritizedRef.category}`,
    }
  }

  if (!args.hasStartKeyframe && args.readyImageMaterials[0]) {
    return {
      kind: 'input_material',
      semanticRole: 'reference_image',
      inputMaterial: args.readyImageMaterials[0],
      sourceLabel: 'input_material.image_ref_in',
    }
  }

  return { kind: 'none', semanticRole: 'none', sourceLabel: 'none' }
}

export function hasVisualConditioningInput(args: {
  keyframeTimeline: KeyframeTimelineEntry[]
  inputAssetRefs: InputAssetRef[]
  readyImageMaterials: Array<Extract<InputMaterial, { kind: 'IMAGE' }>>
}): boolean {
  if (args.keyframeTimeline.some((e) => e.role === 'start' || e.role === 'middle' || e.role === 'end')) {
    return true
  }
  if (refsWithImageAssets(args.inputAssetRefs).length > 0) return true
  if (args.readyImageMaterials.length > 0) return true
  return false
}

export function buildVisualConditioningDiagnostics(args: {
  resolved: ResolvedVisualConditioning
  inputAssetRefs: InputAssetRef[]
  effectivePromptSource: 'ORIGINAL' | 'ENHANCED'
}): VisualConditioningDiagnostics {
  const refImages = refsWithImageAssets(args.inputAssetRefs)
  return {
    has_first_frame: args.resolved.semanticRole === 'first_frame',
    reference_asset_count: refImages.length,
    reference_asset_types: refImages.map((r) => r.category),
    visual_conditioning_source: args.resolved.sourceLabel,
    visual_semantic_role: args.resolved.semanticRole,
    prompt_source: args.effectivePromptSource === 'ENHANCED' ? 'enhanced' : 'original',
  }
}
